import { useRef, useEffect } from 'react';

const SECTIONS_TOTAL = 7; // 0 (hero) → 7 (contact)
const FADE_MS = 2200;     // must outlast the CSS opacity transition (2s)

function makeBolt(W, H) {
  const pts = [];
  let x = 80 + Math.random() * (W - 160), y = 0;
  pts.push({ x, y });
  while (y < H * 0.65) {
    x += (Math.random() - 0.5) * 70;
    y += 25 + Math.random() * 45;
    pts.push({ x, y });
  }
  return pts;
}

// ── Layer factory ──────────────────────────────────────────────
function makeLayer(count, lenMin, lenMax, speedMin, speedMax, opMin, opMax, W, H) {
  return Array.from({ length: count }, () => ({
    x:       Math.random() * W,
    y:       Math.random() * H,
    len:     lenMin + Math.random() * (lenMax - lenMin),
    speed:   speedMin + Math.random() * (speedMax - speedMin),
    opacity: opMin + Math.random() * (opMax - opMin),
  }));
}

// ── Glass drop factory ────────────────────────────────────────
function makeGlassDrop(W, H) {
  return {
    x:      60 + Math.random() * (W - 120),
    y:      30 + Math.random() * (H * 0.45),
    r:      0,
    maxR:   4 + Math.random() * 11,
    phase:  'grow',   // grow → wait → slide
    waitT:  0,
    velY:   0,
    trail:  [],
    op:     0.25 + Math.random() * 0.3,
  };
}

export default function RainCanvas({ active, dark, sectionIndex, soft = false, weatherBoost = 0 }) {
  const canvasRef      = useRef(null);
  const darkRef        = useRef(dark);
  const sectionRef     = useRef(sectionIndex);
  const boostRef       = useRef(weatherBoost);
  const controlRef     = useRef(null);
  darkRef.current      = dark;
  sectionRef.current   = sectionIndex ?? 0;
  boostRef.current     = weatherBoost;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Reduced motion: no animation loop at all — the canvas stays empty.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Logical (CSS-pixel) size; the backing store is DPR-scaled so rain
    // stays crisp on retina screens.
    let vw = window.innerWidth;
    let vh = window.innerHeight;
    const W = () => vw;
    const H = () => vh;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      vw = window.innerWidth;
      vh = window.innerHeight;
      canvas.width  = Math.round(vw * dpr);
      canvas.height = Math.round(vh * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    // ── 3 parallax layers ────────────────────────────────────
    //           count  lenMin lenMax  spdMin spdMax  opMin  opMax
    let far  = makeLayer(80,  5,  10,  1.2, 2.2,  0.04, 0.10, W(), H());
    let mid  = makeLayer(110, 11, 20,  3.2, 5.5,  0.08, 0.18, W(), H());
    let near = makeLayer(55,  20, 35,  6.5, 9.5,  0.14, 0.28, W(), H());

    // ── Splash particles ─────────────────────────────────────
    const splashPool = [];

    const spawnSplash = (x, y) => {
      const n = 3 + Math.floor(Math.random() * 3);
      for (let i = 0; i < n; i++) {
        splashPool.push({
          x, y,
          vx: (Math.random() - 0.5) * 2.5,
          vy: -(0.8 + Math.random() * 2.2),
          life: 1.0,
        });
      }
      if (splashPool.length > 300) splashPool.splice(0, splashPool.length - 300);
    };

    const advanceDrop = (d, intensity) => {
      d.y += d.speed * (0.6 + intensity * 0.7);
      d.x -= d.speed * 0.18;
      if (d.y > H() + d.len) {
        spawnSplash(d.x, H());
        d.y = -d.len;
        d.x = Math.random() * (W() + 60);
      }
    };

    // ── Glass drops ──────────────────────────────────────────
    const glassDrops = [];
    const glassTimers = [];
    for (let i = 0; i < 7; i++) {
      glassTimers.push(setTimeout(() => glassDrops.push(makeGlassDrop(W(), H())), Math.random() * 6000));
    }

    // ── Thunder ──────────────────────────────────────────────
    const thunder = { flash: 0, bolt: null, boltOp: 0 };
    let thunderTimer;

    const scheduleThunder = (intensity) => {
      const base = 18000 - intensity * 13000; // 18s at 0 → 5s at 1
      thunderTimer = setTimeout(triggerThunder, base + Math.random() * 6000);
    };

    function triggerThunder() {
      const intensity = Math.min(1, sectionRef.current / SECTIONS_TOTAL + boostRef.current);
      thunder.flash  = 0.22 + intensity * 0.18 + Math.random() * 0.12;
      thunder.bolt   = makeBolt(W(), H());
      thunder.boltOp = 1;
      if (Math.random() < 0.5) setTimeout(() => { thunder.flash = 0.1 + Math.random() * 0.1; }, 130);
      scheduleThunder(intensity);
    }

    // ── Smooth intensity ─────────────────────────────────────
    let smoothIntensity = 0;

    // ── Draw loop — only runs while the rain is visible ──────
    let raf = 0;
    let running = false;

    const draw = () => {
      ctx.clearRect(0, 0, W(), H());

      const target = Math.min(1, sectionRef.current / SECTIONS_TOTAL + boostRef.current);
      smoothIntensity += (target - smoothIntensity) * 0.015;
      const intensity = smoothIntensity;

      const [r, g, b] = darkRef.current ? [180, 215, 255] : [60, 100, 180];
      const opScale   = 0.35 + intensity * 0.65;

      // Far layer (small, faint — atmospheric depth)
      for (const d of far) {
        ctx.beginPath();
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${d.opacity * opScale * 0.6})`;
        ctx.lineWidth = 0.4;
        ctx.moveTo(d.x, d.y);
        ctx.lineTo(d.x - d.len * 0.12, d.y + d.len);
        ctx.stroke();
        advanceDrop(d, intensity);
      }

      // Mid layer
      for (const d of mid) {
        ctx.beginPath();
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${d.opacity * opScale})`;
        ctx.lineWidth = 0.6;
        ctx.moveTo(d.x, d.y);
        ctx.lineTo(d.x - d.len * 0.18, d.y + d.len);
        ctx.stroke();
        advanceDrop(d, intensity);
      }

      // Near layer (large, fast — foreground)
      for (const d of near) {
        ctx.beginPath();
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${d.opacity * opScale})`;
        ctx.lineWidth = 1.0;
        ctx.moveTo(d.x, d.y);
        ctx.lineTo(d.x - d.len * 0.2, d.y + d.len);
        ctx.stroke();
        advanceDrop(d, intensity);
      }

      // Splash
      for (let i = splashPool.length - 1; i >= 0; i--) {
        const s = splashPool[i];
        ctx.beginPath();
        ctx.arc(s.x, s.y, 0.8, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${s.life * opScale * 0.5})`;
        ctx.fill();
        s.x   += s.vx;
        s.y   += s.vy;
        s.vy  += 0.12;
        s.life -= 0.055;
        if (s.life <= 0) splashPool.splice(i, 1);
      }

      // Glass drops
      const now = performance.now();
      for (let i = glassDrops.length - 1; i >= 0; i--) {
        const gd = glassDrops[i];

        if (gd.phase === 'grow') {
          gd.r += 0.04;
          if (gd.r >= gd.maxR) {
            gd.phase = 'wait';
            gd.waitT = now + 600 + Math.random() * 2400;
          }
        } else if (gd.phase === 'wait' && now >= gd.waitT) {
          gd.phase = 'slide';
        } else if (gd.phase === 'slide') {
          gd.velY += 0.14;
          gd.trail.push({ x: gd.x, y: gd.y });
          if (gd.trail.length > 18) gd.trail.shift();
          gd.y += gd.velY;
          gd.x += (Math.random() - 0.5) * 0.25;
        }

        // Trail (thin streak left on the glass)
        if (gd.trail.length > 1) {
          ctx.beginPath();
          ctx.moveTo(gd.trail[0].x, gd.trail[0].y);
          for (const pt of gd.trail) ctx.lineTo(pt.x, pt.y);
          ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${gd.op * 0.18 * opScale})`;
          ctx.lineWidth   = gd.r * 0.45;
          ctx.lineCap     = 'round';
          ctx.stroke();
          ctx.lineCap     = 'butt';
        }

        // Drop body (stretched ellipse when sliding)
        const stretchY = gd.phase === 'slide' ? Math.min(gd.velY * 0.25, 1.8) : 0;
        ctx.beginPath();
        ctx.ellipse(gd.x, gd.y, gd.r * 0.85, gd.r + stretchY, 0, 0, Math.PI * 2);
        ctx.fillStyle   = `rgba(${r}, ${g}, ${b}, ${gd.op * 0.12 * opScale})`;
        ctx.fill();
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${gd.op * 0.45 * opScale})`;
        ctx.lineWidth   = 0.5;
        ctx.stroke();

        // Highlight
        ctx.beginPath();
        ctx.arc(gd.x - gd.r * 0.28, gd.y - gd.r * 0.28, gd.r * 0.3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,0.22)`;
        ctx.fill();

        if (gd.y > H() + 60) {
          glassDrops.splice(i, 1);
          glassDrops.push(makeGlassDrop(W(), H()));
        }
      }

      // Flash overlay
      if (thunder.flash > 0) {
        ctx.fillStyle = darkRef.current
          ? `rgba(200, 225, 255, ${thunder.flash})`
          : `rgba(120, 160, 255, ${thunder.flash})`;
        ctx.fillRect(0, 0, W(), H());
        thunder.flash = Math.max(0, thunder.flash - 0.025);
      }

      // Lightning bolt
      if (thunder.bolt && thunder.boltOp > 0) {
        const pts = thunder.bolt;
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
        ctx.strokeStyle = `rgba(255,255,255,${thunder.boltOp})`;
        ctx.lineWidth   = 1.5 + thunder.boltOp * 1.5;
        ctx.shadowColor = 'rgba(180,210,255,0.9)';
        ctx.shadowBlur  = 14;
        ctx.stroke();
        ctx.shadowBlur  = 0;
        thunder.boltOp  = Math.max(0, thunder.boltOp - 0.06);
        if (thunder.boltOp === 0) thunder.bolt = null;
      }

      raf = requestAnimationFrame(draw);
    };

    const start = () => {
      if (running) return;
      running = true;
      scheduleThunder(sectionRef.current / SECTIONS_TOTAL);
      raf = requestAnimationFrame(draw);
    };

    const stop = () => {
      if (!running) return;
      running = false;
      cancelAnimationFrame(raf);
      clearTimeout(thunderTimer);
      thunder.flash = 0; thunder.bolt = null; thunder.boltOp = 0;
      ctx.clearRect(0, 0, W(), H());
    };

    controlRef.current = { start, stop };

    return () => {
      controlRef.current = null;
      stop();
      glassTimers.forEach(clearTimeout);
      window.removeEventListener('resize', resize);
    };
  }, []);

  // Start/stop the loop with the `active` prop; on deactivation, keep
  // drawing until the CSS fade-out has finished so the rain doesn't freeze.
  useEffect(() => {
    if (active) {
      controlRef.current?.start();
      return undefined;
    }
    const t = setTimeout(() => controlRef.current?.stop(), FADE_MS);
    return () => clearTimeout(t);
  }, [active]);

  const cls = ['rain-canvas', active ? 'rain-canvas--on' : '', soft ? 'rain-canvas--soft' : '']
    .filter(Boolean).join(' ');
  return <canvas ref={canvasRef} className={cls} />;
}
